import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import IconChevron from "../Icon/IconChevron";
import LinkWrap from "../LinkWrap/component";
import classNames from "classnames";
import NavItem from "../NavItem/component";

const MoreItemsNav = ({
    baseNavSection = "",
    subSection = "",
    allHidden = false,
    moreButtonLabel,
    isExternalHeader = false,
    id = "more-items-list",
    path,
    items,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const buttonRef = useRef();

    useEffect(() => {
        setMenuOpen(false);
    }, [path]);

    /**
     * onClick handler that shows/hides the "More" menu items
     * @return {void}
     */
    const toggleMenuVisibility = useCallback(() => {
        setMenuOpen(!menuOpen);
    }, [menuOpen]);

    const keyUpHandler = (e) => {
        if (e.key === "Escape") {
            setMenuOpen(false);
            buttonRef.current?.focus();
        }
    };

    const getMoreButton = () => {
        //isExternalHeader

        let menuLabel = allHidden ? "Sections" : "More";
        if (moreButtonLabel) {
            menuLabel = moreButtonLabel;
        }

        const ariaLabel = `${menuLabel} from ${baseNavSection} ${
            subSection ? subSection : ""
        }`;

        if (isExternalHeader) {
            const moreUrl = `//www.cbc.ca/${baseNavSection}`;
            return (
                <LinkWrap
                    className="navLink subNavLink"
                    url={moreUrl}
                    external={true}
                >
                    {" "}
                    {menuLabel} &raquo;{" "}
                </LinkWrap>
            );
        }

        return (
            <button
                className="moreButton"
                aria-label={ariaLabel}
                aria-expanded={menuOpen}
                aria-controls={id}
                onClick={toggleMenuVisibility}
                onKeyUp={keyUpHandler}
                ref={buttonRef}
                data-testid="more-button"
                data-cy="more-button"
            >
                {menuLabel} <IconChevron />
            </button>
        );
    };

    let renderNavItems = "";
    const navClasses = classNames("moreItemsNav", {
        open: menuOpen,
        external: isExternalHeader,
    });

    const ulRef = useRef(null);
    const onBlur = (e) => {
        if (!ulRef?.current?.contains(e.relatedTarget)) {
            setMenuOpen(false);
        }
    };
    if (items && items !== undefined) {
        renderNavItems = items.map((item, index) => {
            const itemProps = {
                item: item,
                key: `morenav-${index}`,
                path: path,
                className: "moreNavListItem",
                linkClassName: "moreNavLink",
                onBlur,
                keyUpHandler,
            };
            const isLastItem = index === items.length - 1;
            if (isLastItem) {
                itemProps.ariaLabel = "End of list";
            }
            return (
                <NavItem
                    {...itemProps}
                    toggleMenuVisibility={toggleMenuVisibility}
                />
            );
        });
    }

    return (
        <div className={navClasses} data-cy="more-items-nav">
            {getMoreButton()}
            <ul
                className="moreItemsList"
                data-cy="more-items-list"
                id={id}
                aria-hidden={!menuOpen}
                ref={ulRef}
            >
                {renderNavItems}
            </ul>
        </div>
    );
};

MoreItemsNav.propTypes = {
    baseNavSection: PropTypes.string,
    subSection: PropTypes.string,
    allHidden: PropTypes.bool,
    moreButtonLabel: PropTypes.string,
    isExternalHeader: PropTypes.bool,
    id: PropTypes.string,
};

export default MoreItemsNav;
